/**
 * `useAssistant` — one conversation about one subject (#765, spec §8.1).
 *
 * Owns the ask lifecycle (`idle → asking → answered | failed | timeout`),
 * the thread history, the polling of an in-flight turn and the quota. The
 * panel renders it; it holds no JSX.
 *
 * Two guards the spec insists on:
 * - a generation counter keyed by `(orgId, subjectKey, seq)`: an answer that
 *   arrives after the organization or subject changed is dropped, otherwise
 *   the mutation would hand another organization's answer to the panel;
 * - polling is enabled only while the turn's organization is still selected
 *   (`GET /turns/:id` in the new organization would 404 and toast).
 *
 * History is the source of truth once it refetches: the turn just asked is
 * kept locally only until `GET /threads` lists it, so a completed turn is
 * never shown twice — but until then a verdict polled HERE outranks the
 * stale "in progress" copy history still holds.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '@/lib/api';
import { isTransientFailure } from '@/lib/query-retry';
import { useAccessChanges } from '@/hooks/useAccessChange';
import {
  rememberAskedQuestions,
  wasQuestionAsked,
} from '@/lib/assistant/asked-questions';
import { orgFiltersKey, sameOrgFilters } from '@/lib/assistant/org-filters';
import {
  ASSISTANT_ORG_DEADLINE_MS,
  ASSISTANT_RECORD_TIMEOUT_MS,
  assistantQuotaOptions,
  assistantQuotaQueryKey,
  assistantThreadsOptions,
  assistantThreadsQueryKey,
  assistantTurnOptions,
  isAssistantTurnTerminal,
} from '@/lib/query-options/assistant';
import {
  buildSubjectKey,
  type AssistantAnswer,
  type AssistantAskRequest,
  type AssistantOrgFilters,
  type AssistantQuota,
  type AssistantQuotaResponse,
  type AssistantScope,
  type AssistantSubject,
  type AssistantThread,
  type AssistantTurnStatus,
} from '@aica/api-types/schemas';

export type AssistantAskState = 'idle' | 'asking' | 'answered' | 'failed' | 'timeout';

/** One turn as the panel renders it — from history or from the ask in flight. */
export interface AssistantTurnView {
  turnId: string;
  /**
   * The thread the turn belongs to: the one the ask continued, the one the
   * answer named, or the one history lists it in. `null` while an ask is
   * opening a thread the answer has not named yet. Only the shown thread's
   * local copies join the list (Codex P2, round 18).
   */
  threadId: string | null;
  question: string;
  /**
   * What was typed here, for a turn asked in this tab — `question` is then
   * the stored, checked version (#2449). Only `isRepeat` reads it: the same
   * text typed again is a repeat even when the shown one carries a mask.
   */
  typed?: string;
  createdAt: string;
  status: AssistantTurnStatus;
  /**
   * The verdict — `null` until the turn is terminal. The same-key replay
   * reaching the turn mid-flight answers with a pending DTO that carries
   * nothing to render: it is not attached, the panel shows progress instead
   * (Codex P2, round 20). History lists an in-flight turn with `null` already.
   */
  answer: AssistantAnswer | null;
  /**
   * The filters the question went out under (`org` only; `null` elsewhere and
   * for an unfiltered question). Filters belong to the turn, not the thread:
   * without them «5 records in August» cannot be read.
   */
  filters: AssistantOrgFilters | null;
}

export interface UseAssistantParams {
  subject: AssistantSubject;
  apiClient: ApiClient;
  selectedOrgId: string | null;
  /** `false` while the panel is closed: no history read, no quota peek. */
  enabled?: boolean;
}

export interface UseAssistantResult {
  state: AssistantAskState;
  /** Machine code of the last failure (§2.2 tables) — the panel maps it to a toast key. */
  errorCode: string | null;
  /** The last failure in full — `errorCode` plus, for a ceiling, the window that tripped. */
  refusal: AssistantRefusal | null;
  /**
   * A refusal that blocks the whole panel rather than one question — the
   * tier, a disabled feature, no access — seen on the quota peek or an ask.
   */
  blockedCode: string | null;
  /**
   * The kill switch stops this contour (#1059): the composer closes, the
   * history stays readable — a temporary stop is not a lost entitlement.
   */
  paused: boolean;
  threadId: string | null;
  turns: AssistantTurnView[];
  /** Every thread history lists for the subject — the page's thread list. */
  threads: readonly AssistantThread[];
  isLoadingHistory: boolean;
  /**
   * `GET /threads` failed after its retries. The panel must not read this as
   * "no conversations yet": an ask without `threadId` would continue the
   * user's latest thread with its turns invisible (Codex P2, round 2).
   */
  historyError: boolean;
  reloadHistory: () => void;
  quota: AssistantQuota | null;
  /**
   * Whether `question` was already asked in the shown thread: in the turns
   * on screen, among the texts typed here (history returns a question
   * minimized, §6.2 — Codex P2, round 10), or in this tab's memory of what
   * history has listed for the thread beyond the 20 turns `GET /threads`
   * returns (Codex P2, rounds 19 and 20). The panel asks for confirmation
   * then (§8.2).
   */
  isRepeat: (question: string) => boolean;
  /** The turn being polled, if any. */
  activeTurnId: string | null;
  /**
   * Anything PAID is still running, so moving the topic would abandon it
   * (#922). Three ways, not one:
   * - `asking` — the POST is still out;
   * - a turn accepted by the server and being polled;
   * - `timeout` — polling hit the CLIENT deadline, so `activeTurnId` is
   *   already cleared while the server may still finish the turn.
   *
   * Read by the drawer: while this is true the topic does not move, because a
   * move bumps the generation and drops the ask's answer, the poll and the
   * deadline. A lost ask is not among them: its `Idempotency-Key` is kept per
   * organization and subject and survives a move, so the retry back on its
   * subject still rides it (Codex P1, round 13; #937).
   */
  hasUnsettledWork: boolean;
  ask: (question: string) => Promise<void>;
  /** The next question opens a new thread instead of continuing this one. */
  startNewThread: () => void;
  /**
   * Shows `threadId` and continues it. Refused while a turn is in flight, for
   * the reason `startNewThread` is: the poll it would drop is a paid answer.
   */
  openThread: (threadId: string) => void;
}

interface ActiveTurn {
  turnId: string;
  orgId: string;
  scope: AssistantScope;
  startedAt: number;
  /** Picked up from history — asked elsewhere (another tab, the other session on the page). */
  resumed?: boolean;
}

export type AssistantQuotaWindow = 'minute' | 'hour' | 'day';

/** A refused `POST /ask` as the panel words it: the code and, for a ceiling, which window tripped. */
export interface AssistantRefusal {
  code: string;
  /** `quota_exceeded` only (§2.2): the window that tripped; null when the body named none — the day. */
  window: AssistantQuotaWindow | null;
  /** `quota_exceeded` only: when that window resets. */
  resetAt: string | null;
  /**
   * The verdict of a turn this session picked up from history rather than
   * asked. Shown on the turn itself, but announced only by the session that
   * asked: on `/assistant` the page and the drawer can both follow one
   * whole-base thread, and each would toast the same failure (code-review, #1312).
   */
  resumed?: boolean;
}

const QUOTA_WINDOWS: ReadonlySet<string> = new Set(['minute', 'hour', 'day']);

/**
 * The machine code of a failed `POST /ask` (§2.2): edge-api and workflows-api
 * refusals carry `{ error, code }`; a 429 without a body is the daily ceiling.
 */
export function assistantErrorCode(error: unknown): string {
  return assistantRefusal(error).code;
}

/**
 * The whole verdict of a failed ask. A `quota_exceeded` body also names the
 * window (`minute` | `hour` | `day`) and its `resetAt`: the per-minute and
 * hourly ceilings are not "try again tomorrow", and reducing them to the
 * code told the user exactly that (Codex P2, round 3).
 */
export function assistantRefusal(error: unknown): AssistantRefusal {
  // The client's own deadline aborted the request: no verdict from the
  // server at all (Codex P2, round 12).
  const name = (error as { name?: unknown } | null)?.name;
  if (name === 'AbortError' || name === 'TimeoutError') {
    return { code: 'request_timeout', window: null, resetAt: null };
  }
  const failure = error as { status?: unknown; data?: unknown } | null;
  const data = failure?.data as
    { code?: unknown; window?: unknown; resetAt?: unknown } | null | undefined;
  let code = 'generic';
  if (data && typeof data.code === 'string' && data.code) code = data.code;
  // The assistant's own ceilings name themselves (`code: 'quota_exceeded'`);
  // a bare 429 is the global limiter — try again in a while, not tomorrow
  // (owner's review, 17.09).
  else if (failure?.status === 429) code = 'rate_limited';
  else if (failure?.status === 403) code = 'forbidden';
  if (code !== 'quota_exceeded') return { code, window: null, resetAt: null };
  return {
    code,
    window:
      typeof data?.window === 'string' && QUOTA_WINDOWS.has(data.window)
        ? (data.window as AssistantQuotaWindow)
        : null,
    resetAt: typeof data?.resetAt === 'string' ? data.resetAt : null,
  };
}

/**
 * A failure with no verdict on the turn: the request never got an answer
 * (network), or the server answered 5xx — which the edge also words when
 * its proxy to workflows-api timed out and the row was KEPT
 * (`llm_unavailable`), or when the answer exists but its read failed
 * (`context_unavailable`). A 4xx is a verdict: rolled back or never created.
 */
/** The global limiter's 429: no verdict on anything, ask again in a while. */
function isRateLimited(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 429;
}

/**
 * 5xx refusals that say the turn was never written: the question's PII pass
 * failed before the row (#2364). A verdict, not a doubt — a replay would only
 * pay for the pass again, and history has no turn to find.
 */
const NEVER_WRITTEN_CODES: ReadonlySet<string> = new Set(['question_unchecked']);

export function isAmbiguousFailure(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== 'number') return true;
  return status >= 500 && !NEVER_WRITTEN_CODES.has(assistantErrorCode(error));
}

/** How often a stopped contour's panel asks whether the switch was lifted. */
const PAUSED_RECHECK_MS = 60 * 1000;

/**
 * The contours the kill switch stops, as `/quota` last told. edge-api sends
 * them on its 503 as well (#1076 review): the limiter being down — likely
 * during the incident that prompted the stop — must not hide the stop.
 */
function quotaPausedScopes(
  data: AssistantQuotaResponse | undefined,
  error: unknown
): readonly string[] {
  const fromError = (error as { data?: { pausedScopes?: unknown } } | null)?.data
    ?.pausedScopes;
  if (Array.isArray(fromError)) {
    return fromError.filter((scope): scope is string => typeof scope === 'string');
  }
  return data?.pausedScopes ?? [];
}

const BLOCKING_CODES: ReadonlySet<string> = new Set([
  'tier_not_eligible',
  'feature_disabled',
  'forbidden',
]);

/**
 * A spent quota is re-read this long after its `resetAt` — never sooner than
 * the floor (a `resetAt` already in the past must not become a tight loop),
 * never beyond what a timer can hold.
 */
const QUOTA_RESET_GRACE_MS = 5_000;
const QUOTA_RESET_MIN_DELAY_MS = 30_000;
const MAX_TIMER_DELAY_MS = 2_147_483_647;
/** The history read that reconciles a lost ask waits this long — it must not hold the panel. */
const RECONCILE_READ_MS = 15_000;
/**
 * Lost asks kept at once. An entry leaves when history lists its turn or a
 * verdict arrives — and one asked on a subject never revisited, or in an
 * organization left for good, would otherwise stay for the drawer's life.
 * Past this many the oldest goes: a question lost twenty questions ago is no
 * longer a retry anyone is about to make.
 */
const MAX_LOST_ASKS = 20;

/** `promise`'s value, or `null` once `ms` have passed or it rejected. */
function withinMs<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      }
    );
  });
}

/** This client's own row for an ask in flight, until the server names the real turn. */
const PLACEHOLDER_PREFIX = 'pending:';

/** The thread holding the turn created under `idempotencyKey` — a lost ask's own turn. */
function findLostTurnThread(
  threads: readonly AssistantThread[],
  idempotencyKey: string
): AssistantThread | null {
  return (
    threads.find((thread) =>
      thread.turns.some((turn) => turn.idempotencyKey === idempotencyKey)
    ) ?? null
  );
}

/**
 * The thread's turn polling may pick up: still in flight, not settled by this
 * client, and still able to answer within the client deadline. One past the
 * deadline stays "in progress" in the list until the server's own
 * `stale_timeout` settles it. Shared by the resume effect and the lost-ask
 * recovery, which must agree on which thread is in front.
 */
function resumableTurn(
  thread: AssistantThread,
  scope: AssistantSubject['scope'],
  settledHere: ReadonlySet<string>
) {
  const deadline =
    scope === 'org' ? ASSISTANT_ORG_DEADLINE_MS : ASSISTANT_RECORD_TIMEOUT_MS;
  const now = Date.now();
  return thread.turns.find(
    (turn) =>
      !isAssistantTurnTerminal(turn.status) &&
      !settledHere.has(turn.turnId) &&
      Date.parse(turn.createdAt) + deadline > now
  );
}

function latestTurnTime(thread: AssistantThread): number {
  return thread.turns.reduce((max, turn) => Math.max(max, Date.parse(turn.createdAt)), 0);
}

/** The thread's most recent turn — the one whose filters it was last asked under. */
function latestTurnOf(
  thread: AssistantThread
): AssistantThread['turns'][number] | undefined {
  return thread.turns.reduce<AssistantThread['turns'][number] | undefined>(
    (latest, turn) =>
      latest === undefined || Date.parse(turn.createdAt) > Date.parse(latest.createdAt)
        ? turn
        : latest,
    undefined
  );
}

function toTurnView(
  turn: AssistantThread['turns'][number],
  threadId: string
): AssistantTurnView {
  return {
    turnId: turn.turnId,
    threadId,
    question: turn.question,
    createdAt: turn.createdAt,
    status: turn.status,
    // `GET /threads` lists an in-flight turn with `answer: null` already.
    answer: turn.answer,
    filters: turn.filters,
  };
}

function toView(thread: AssistantThread): AssistantTurnView[] {
  return thread.turns.map((turn) => toTurnView(turn, thread.threadId));
}

/**
 * What a render shows before the hook's state belongs to its context (see
 * `stale` inside the hook): nothing, and a composer still waiting for history.
 */
const IDLE_RESULT: UseAssistantResult = {
  state: 'idle',
  errorCode: null,
  refusal: null,
  blockedCode: null,
  paused: false,
  threadId: null,
  turns: [],
  threads: [],
  isLoadingHistory: true,
  historyError: false,
  reloadHistory: () => undefined,
  quota: null,
  isRepeat: () => false,
  activeTurnId: null,
  hasUnsettledWork: false,
  ask: () => Promise.resolve(),
  startNewThread: () => undefined,
  openThread: () => undefined,
};

export function useAssistant({
  subject,
  apiClient,
  selectedOrgId,
  enabled = true,
}: UseAssistantParams): UseAssistantResult {
  const queryClient = useQueryClient();
  const subjectKey = buildSubjectKey(subject);
  const contextKey = `${selectedOrgId ?? ''}|${subjectKey}`;
  // What the panel shows is also bound to the viewer's access (E1.5 PR 4b):
  // a change of it drops the turns kept here like a change of context does —
  // history, asked again under the new access, brings back what it still
  // covers. Lost asks keep `contextKey` alone: the same question asked again
  // must still ride its key, never pay twice.
  const accessChanges = useAccessChanges();
  const viewKey = `${contextKey}|${accessChanges}`;

  const [state, setState] = useState<AssistantAskState>('idle');
  const [refusal, setRefusal] = useState<AssistantRefusal | null>(null);
  const errorCode = refusal?.code ?? null;
  const [threadId, setThreadId] = useState<string | null>(null);
  const [freshThread, setFreshThread] = useState(false);
  const [activeTurn, setActiveTurn] = useState<ActiveTurn | null>(null);
  const [localTurns, setLocalTurns] = useState<AssistantTurnView[]>([]);

  const generationRef = useRef(0);
  const contextRef = useRef(viewKey);
  // A context switch is settled by the effect below — one render after the
  // props changed. That render shows and subscribes nothing of the old
  // context: the panel, keyed by the new subject, would otherwise flash
  // record A's turns under record B, and A's history would be read under
  // the new organization before the provider closes the drawer (Codex P2,
  // round 24). Resume waits for the clean render too.
  const stale = contextRef.current !== viewKey;
  // Turns this client already brought to an end: the verdict was polled here,
  // or the client deadline passed here. History keeps listing both as in
  // flight until it refetches (or, for a deadline, until the server marks the
  // turn stale), and resuming one again is a loop — a zero-delay timeout that
  // re-enables polling every tick (Codex P1, round 2), or a cached terminal
  // answer that settles, clears and re-activates on every render (Codex P1,
  // round 3). Cleared with the context: another organization's ids are moot.
  const settledHereRef = useRef<Set<string>>(new Set());
  // The turn whose client deadline passed here and when: released once
  // history — re-read at the server deadline — shows its verdict, or no
  // longer lists it at all.
  const timedOutRef = useRef<{ turnId: string; at: number } | null>(null);
  // Questions that got NO verdict — the request and its replay both lost —
  // by the key each went out under, until history lists its turn or a
  // verdict arrives. More than one can be outstanding: a later ask must not
  // discard an earlier one's key, or its paid turn could never be recognized
  // (Codex P2, round 19). The same text sent again reuses its key: the spec's
  // «повтор после сетевого таймаута», answered with the turn the server kept,
  // never a second charge (Codex P1, round 13). «The same» is the same text
  // under the same filters: the whole-base page changes filters without
  // changing the subject key, and the old key would fetch the answer computed
  // under the old filters (code-review, #1312).
  //
  // Each entry also names the context — organization and subject — it was
  // asked in: the same text asked on another record is a different question
  // and must neither pick up this key nor be recovered from that record's
  // history (#937). So the map survives a change of context: back on its
  // subject, the retry still rides the key; another organization's entries
  // match nothing there.
  //
  // One rule settles an entry: history lists its key ⇒ the key is spent. The
  // recovery effect below applies it, and re-runs on `lostAsksVersion`, so an
  // entry recorded after history loaded — by an ask that comes back late — is
  // judged too. A verdict on the ask (an answer, a refusal) spends it as well.
  const lostAsksRef = useRef<
    Map<string, { contextKey: string; question: string; filters: string }>
  >(new Map());
  const [lostAsksVersion, setLostAsksVersion] = useState(0);
  const keepLostAsk = useCallback(
    (key: string, asked: { contextKey: string; question: string; filters: string }) => {
      const lost = lostAsksRef.current;
      lost.delete(key);
      lost.set(key, asked);
      for (const oldest of lost.keys()) {
        if (lost.size <= MAX_LOST_ASKS) break;
        lost.delete(oldest);
      }
      setLostAsksVersion((version) => version + 1);
    },
    []
  );
  const spendLostAsk = useCallback((key: string) => {
    if (lostAsksRef.current.delete(key)) setLostAsksVersion((version) => version + 1);
  }, []);

  // Organization or subject changed: everything in flight belongs to the old
  // context and is dropped — including the answer a pending `ask` may still
  // deliver (the generation moved on, so it is ignored). Lost asks stay: each
  // names its own context (above).
  useEffect(() => {
    if (contextRef.current === viewKey) return;
    contextRef.current = viewKey;
    generationRef.current += 1;
    settledHereRef.current = new Set();
    timedOutRef.current = null;
    setState('idle');
    setRefusal(null);
    setThreadId(null);
    setFreshThread(false);
    setActiveTurn(null);
    setLocalTurns([]);
  }, [viewKey]);

  const threadsOptions = assistantThreadsOptions(apiClient, selectedOrgId, subject);
  const threadsQuery = useQuery({
    ...threadsOptions,
    enabled: enabled && !stale && threadsOptions.enabled,
  });

  const quotaOptions = assistantQuotaOptions(apiClient, selectedOrgId);
  const quotaQuery = useQuery({
    ...quotaOptions,
    enabled: enabled && !stale && quotaOptions.enabled,
    // While a contour is stopped the quota is re-read on a timer: lifting
    // the switch has to reopen a panel left open (#1076 review).
    refetchInterval: (query) =>
      quotaPausedScopes(query.state.data, query.state.fetchFailureReason).length > 0
        ? PAUSED_RECHECK_MS
        : false,
  });

  // A spent day reopens by itself: the quota is re-read once `resetAt` has
  // passed, so a drawer left open overnight does not keep the composer
  // closed on the cached zero — the peek has no reset timer of its own and
  // does not refetch on focus (Codex P2, round 4).
  useEffect(() => {
    const quota = quotaQuery.data;
    if (!selectedOrgId || !quota || quota.remainingToday !== 0 || !quota.resetAt) return;
    const resetAt = Date.parse(quota.resetAt);
    if (Number.isNaN(resetAt)) return;
    const orgId = selectedOrgId;
    const delay = Math.min(
      Math.max(resetAt + QUOTA_RESET_GRACE_MS - Date.now(), QUOTA_RESET_MIN_DELAY_MS),
      MAX_TIMER_DELAY_MS
    );
    const timer = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: assistantQuotaQueryKey(orgId) });
    }, delay);
    return () => clearTimeout(timer);
    // `dataUpdatedAt`: a re-read that returns the same body keeps the same
    // data reference (structural sharing) — the next timer must still be
    // armed, or the composer stays closed on the cached zero (owner's
    // review, 17.09).
  }, [quotaQuery.data, quotaQuery.dataUpdatedAt, selectedOrgId, queryClient]);

  // The thread the panel shows: the one the last answer named, else the most
  // recently active one for this subject (§3.7 — "the user's last thread").
  const currentThread = useMemo(() => {
    const threads = threadsQuery.data ?? [];
    if (freshThread) return null;
    if (threadId) return threads.find((thread) => thread.threadId === threadId) ?? null;
    return threads.reduce<AssistantThread | null>(
      (best, thread) =>
        best === null || latestTurnTime(thread) > latestTurnTime(best) ? thread : best,
      null
    );
  }, [threadsQuery.data, threadId, freshThread]);

  // A turn still in flight in history (panel closed and reopened, or another
  // tab asked) resumes polling instead of provoking a second paid question.
  useEffect(() => {
    // Not while this client's own ask is out: history may already list its
    // row, and resuming it would fork the lifecycle — a second poll settling
    // before the POST returns, the placeholder never renamed (owner's
    // review, 17.09).
    if (stale || state === 'asking' || activeTurn || !selectedOrgId || !currentThread)
      return;
    const inFlight = resumableTurn(currentThread, subject.scope, settledHereRef.current);
    if (!inFlight) return;
    setActiveTurn({
      turnId: inFlight.turnId,
      orgId: selectedOrgId,
      scope: subject.scope,
      startedAt: Date.parse(inFlight.createdAt),
      resumed: true,
    });
    setThreadId(currentThread.threadId);
    setState('asking');
  }, [stale, state, activeTurn, currentThread, selectedOrgId, subject.scope]);

  const pollOptions = assistantTurnOptions(
    apiClient,
    activeTurn?.orgId ?? null,
    activeTurn ?? { turnId: '', scope: subject.scope, startedAt: 0 }
  );
  const turnQuery = useQuery({
    ...pollOptions,
    enabled:
      pollOptions.enabled && !stale && !!activeTurn && activeTurn.orgId === selectedOrgId,
  });

  /**
   * A verdict goes into the list. The local copy takes it; a turn known only
   * from history has no local copy yet, so `view` (its history entry) seeds
   * one — the answer shows at once and outranks the stale "in progress" copy
   * until history refetches (Codex P1, round 3). The ask state is not
   * touched here: a verdict for an old turn must not speak for a newer ask.
   */
  const recordVerdict = useCallback(
    (answer: AssistantAnswer, orgId: string, view?: AssistantTurnView) => {
      setLocalTurns((prev) => {
        const verdict = { status: answer.status, answer };
        if (prev.some((turn) => turn.turnId === answer.turnId)) {
          return prev.map((turn) =>
            turn.turnId === answer.turnId ? { ...turn, ...verdict } : turn
          );
        }
        return view ? [...prev, { ...view, ...verdict }] : prev;
      });
      void queryClient.invalidateQueries({
        queryKey: assistantThreadsQueryKey(orgId, subject),
      });
    },
    [queryClient, subject]
  );

  /** The ask in progress reached its verdict — from `POST /ask` itself or from polling. */
  const settle = useCallback(
    (
      answer: AssistantAnswer,
      orgId: string,
      resumed?: AssistantTurnView,
      pickedUp = false
    ) => {
      setState(answer.status === 'completed' ? 'answered' : 'failed');
      setRefusal(
        answer.status === 'completed' || !answer.errorCode
          ? null
          : {
              code: answer.errorCode,
              window: null,
              resetAt: null,
              ...(pickedUp ? { resumed: true } : {}),
            }
      );
      recordVerdict(answer, orgId, resumed);
    },
    [recordVerdict]
  );

  // Polling reached a terminal status.
  useEffect(() => {
    const polled = turnQuery.data;
    // A verdict that lands in the switch frame belongs to the old context and
    // would overwrite the reset the context effect just made (Codex P2,
    // round 25). The next render has no active turn to match it against.
    if (stale || !activeTurn || !polled || polled.turnId !== activeTurn.turnId) return;
    if (!isAssistantTurnTerminal(polled.status)) return;
    settledHereRef.current.add(activeTurn.turnId);
    setActiveTurn(null);
    const resumed = currentThread?.turns.find(
      (turn) => turn.turnId === activeTurn.turnId
    );
    settle(
      polled,
      activeTurn.orgId,
      resumed && currentThread ? toTurnView(resumed, currentThread.threadId) : undefined,
      activeTurn.resumed
    );
  }, [stale, turnQuery.data, activeTurn, settle, currentThread]);

  // Polling met a failure that will not change — 404, the record and its
  // turn are gone; 403, no longer visible. `retryTransientOnly` already
  // refuses to repeat it, but the interval would ask again from the last
  // "running" copy every two seconds until the client deadline, behind a
  // thinking skeleton (Codex P2, round 8). The failure is the verdict:
  // polling stops, the local copy goes, history (the truth) is re-read and
  // the user is told.
  useEffect(() => {
    const error = turnQuery.error;
    // Same as the verdict above: an old 403 in the switch frame would leave
    // the new record's panel blocked (Codex P2, round 25).
    // A 429 of the global limiter is no verdict on the turn either: the
    // interval asks again (owner's review, 17.09).
    if (
      stale ||
      !activeTurn ||
      !error ||
      isTransientFailure(error) ||
      isRateLimited(error)
    )
      return;
    settledHereRef.current.add(activeTurn.turnId);
    setActiveTurn(null);
    setLocalTurns((prev) => prev.filter((turn) => turn.turnId !== activeTurn.turnId));
    void queryClient.invalidateQueries({
      queryKey: assistantThreadsQueryKey(activeTurn.orgId, subject),
    });
    setState('failed');
    setRefusal({
      ...assistantRefusal(error),
      ...(activeTurn.resumed ? { resumed: true } : {}),
    });
  }, [stale, turnQuery.error, activeTurn, queryClient, subject]);

  // Polling stopped without a verdict: the scope's deadline passed. The turn
  // stays in flight on the server until it settles it — ten minutes after
  // `createdAt`, in the listing itself — so history is read now (it lists
  // the turn) and again at that moment (`assistantHistoryRefetchMs`); the
  // timeout is released below once the verdict is in the list.
  useEffect(() => {
    if (!activeTurn) return;
    const deadline =
      activeTurn.scope === 'org'
        ? ASSISTANT_ORG_DEADLINE_MS
        : ASSISTANT_RECORD_TIMEOUT_MS;
    const remaining = activeTurn.startedAt + deadline - Date.now();
    const timer = setTimeout(
      () => {
        settledHereRef.current.add(activeTurn.turnId);
        timedOutRef.current = { turnId: activeTurn.turnId, at: Date.now() };
        setActiveTurn((current) =>
          current?.turnId === activeTurn.turnId ? null : current
        );
        setState('timeout');
        void queryClient.invalidateQueries({
          queryKey: assistantThreadsQueryKey(activeTurn.orgId, subject),
        });
      },
      Math.max(0, remaining)
    );
    return () => clearTimeout(timer);
  }, [activeTurn, queryClient, subject]);

  const shownThreadId = currentThread?.threadId ?? null;

  /**
   * A lost ask's turn was found in history: the panel shows its thread (the
   * new one it opened after `startNewThread`, too), the kept key is released
   * — a repeat of the same question the user confirms later is a NEW
   * question (Codex P2, round 15) — and the quota is re-read: history
   * carries no count (Codex P2, round 16).
   */
  const recoverLostAsk = useCallback(
    (thread: AssistantThread, orgId: string, idempotencyKey: string) => {
      spendLostAsk(idempotencyKey);
      setThreadId(thread.threadId);
      setFreshThread(false);
      void queryClient.invalidateQueries({ queryKey: assistantQuotaQueryKey(orgId) });
    },
    [queryClient, spendLostAsk]
  );

  // The bounded read below may give up before history answers, and the
  // answer still lands in the cache — or the turn shows up on any later
  // refetch, or the ask itself comes back late, after history was read.
  // Whenever history lists a turn under a kept key, the recovery is the same
  // (Codex P2, round 17); a refusal shown for that ask is withdrawn.
  useEffect(() => {
    const threads = threadsQuery.data;
    // Not in the switch frame: entries outlive a change of subject, and that
    // frame pairs the new subject's cached history with the old one's ask
    // state — a leftover thread or turn would read as "newer" and spend the
    // key without bringing its thread forward (as the effects above, Codex
    // P2 round 25).
    if (stale || !selectedOrgId || !threads || lostAsksRef.current.size === 0) return;
    for (const [key, asked] of [...lostAsksRef.current]) {
      // History is the current context's: another subject's lost ask is
      // recognized when the user is back on it, another organization's never
      // here.
      if (asked.contextKey !== contextKey) continue;
      const found = findLostTurnThread(threads, key);
      if (!found) continue;
      // The recovered thread comes to the front only where the lost ask left
      // the panel: no thread chosen yet, or its own. A newer conversation —
      // one in flight, or one settled in another thread — stays in front, or
      // its paid answer would vanish behind the recovered one (Codex P2,
      // round 22). The key is spent and the quota re-read either way.
      // A turn the resume effect is picking up in the shown thread counts as
      // newer too: both effects run in the same frame, and recovering here
      // would show one thread while polling another (local Codex P2, #937).
      const newer =
        state === 'asking' ||
        activeTurn !== null ||
        (threadId !== null && threadId !== found.threadId) ||
        (currentThread !== null &&
          currentThread.threadId !== found.threadId &&
          resumableTurn(currentThread, subject.scope, settledHereRef.current) !==
            undefined);
      if (newer) {
        spendLostAsk(key);
        void queryClient.invalidateQueries({
          queryKey: assistantQuotaQueryKey(selectedOrgId),
        });
        continue;
      }
      recoverLostAsk(found, selectedOrgId, key);
      setState((current) => (current === 'failed' ? 'idle' : current));
      setRefusal(null);
    }
  }, [
    stale,
    threadsQuery.data,
    selectedOrgId,
    recoverLostAsk,
    spendLostAsk,
    state,
    activeTurn,
    threadId,
    currentThread,
    subject.scope,
    queryClient,
    contextKey,
    lostAsksVersion,
  ]);

  // What history lists — the question as the server stored it, redacted —
  // is remembered per thread beyond the 20 turns it returns, so a repeat
  // asked after the panel reopens is still confirmed. Only that: text typed
  // here is never written down (Codex P2, rounds 19 and 20).
  useEffect(() => {
    const threads = threadsQuery.data;
    if (!selectedOrgId || !threads) return;
    for (const thread of threads) {
      rememberAskedQuestions(
        selectedOrgId,
        thread.threadId,
        thread.turns.map((turn) => turn.question)
      );
    }
  }, [threadsQuery.data, selectedOrgId]);

  const ask = useCallback(
    async (question: string) => {
      if (!selectedOrgId) return;
      const orgId = selectedOrgId;
      const generation = ++generationRef.current;
      const filters = subject.scope === 'org' ? orgFiltersKey(subject.filters) : '';
      // A question that got no verdict before goes out under its own key again.
      const lostKey = [...lostAsksRef.current].find(
        ([, asked]) =>
          asked.contextKey === contextKey &&
          asked.question === question &&
          asked.filters === filters
      )?.[0];
      const idempotencyKey = lostKey ?? crypto.randomUUID();
      const startedAt = Date.now();
      // ONE deadline for the request and its replay: the replay gets what is
      // left, never two minutes of its own (Codex P2, round 13).
      const deadlineAt = startedAt + ASSISTANT_RECORD_TIMEOUT_MS;
      const placeholderId = `${PLACEHOLDER_PREFIX}${idempotencyKey}`;
      // The thread this question continues: the one the last answer named,
      // else the one on screen — not the server's "latest" fallback, which
      // another tab may have moved meanwhile (Codex P2, round 4).
      //
      // A whole-base question never continues a thread last asked under other
      // filters: the model would read that history as if it answered this
      // question. The page moves its form to the shown thread's filters, the
      // drawer's whole-base topic has none — so it is the drawer, reopening a
      // thread the page narrowed, that opens a new one here (code-review, #1312).
      const otherFilters =
        subject.scope === 'org' &&
        currentThread !== null &&
        !sameOrgFilters(latestTurnOf(currentThread)?.filters ?? null, subject.filters);
      const opensThread = freshThread || otherFilters;
      const continued = opensThread ? null : (threadId ?? shownThreadId);
      const body: AssistantAskRequest = {
        subject,
        question,
        ...(continued ? { threadId: continued } : { newThread: opensThread }),
      };

      setState('asking');
      setRefusal(null);
      setLocalTurns((prev) => [
        ...prev,
        {
          turnId: placeholderId,
          threadId: continued,
          question,
          typed: question,
          createdAt: new Date(startedAt).toISOString(),
          status: 'pending',
          answer: null,
          filters: subject.scope === 'org' ? (subject.filters ?? null) : null,
        },
      ]);

      try {
        // The proxy answers within 110 s (§7.5); a connection that stalls past
        // the client deadline would otherwise hold `asking` — composer and
        // new-thread action disabled — for good (Codex P2, round 12). The
        // abort is a failure with no verdict, so the replay below applies.
        const attempt = async (): Promise<AssistantAnswer> => {
          const controller = new AbortController();
          const deadline = setTimeout(
            () => controller.abort(new DOMException('ask deadline', 'TimeoutError')),
            Math.max(0, deadlineAt - Date.now())
          );
          try {
            return await apiClient.assistant.ask(body, idempotencyKey, controller.signal);
          } finally {
            clearTimeout(deadline);
          }
        };
        let answer: AssistantAnswer;
        try {
          answer = await attempt();
        } catch (error) {
          if (
            generationRef.current !== generation ||
            !isAmbiguousFailure(error) ||
            Date.now() >= deadlineAt
          )
            throw error;
          // A failure with no verdict on the turn is settled the way §2.2
          // settles a lost reply: the same request with the same
          // `Idempotency-Key`, once more. The server answers with the turn it
          // kept, in whatever state it reached — never a second LLM call — or,
          // if the row was rolled back, honestly tries again. No history
          // heuristics: the stored question is the redacted one (Codex P1
          // round 4, P2 rounds 5 and 8). The replay's own failure is the verdict.
          answer = await attempt();
        }

        // The quota belongs to the ORGANIZATION, not to this generation: the
        // day was spent whether or not the topic moved on while the ask was
        // out. Accounted for before the generation guard below, or the counter
        // would read one too high until `staleTime` expires and the user could
        // be refused a question the panel still offers (#922).
        //
        // But a stale answer must not WRITE its snapshot. Since the panel can
        // move the topic on purpose while a turn is out (the "ask about the
        // record on screen" offer), two asks can be in flight at once, and each
        // answer carries the count as of the moment its own turn reserved a
        // slot. The later answer landing first, then the earlier one
        // overwriting it, would raise the counter and offer a question the
        // server is about to refuse. So: the current generation stores what it
        // was told, anything else asks the server.
        //
        // The re-read is covered by a test; the raise it also prevents is not.
        // A test written for that race passed against a deliberately broken
        // version too, so it was removed rather than kept as false assurance
        // (Codex P2 raised the race, 21.09).
        //
        // A fresh answer carries the count; the idempotent replay path returns
        // the kept turn through `presentTurn` with `quota: null` — the day may
        // have been spent by it, so the count is re-read (Codex P2, round 9).
        const sameGeneration = generationRef.current === generation;
        const answerQuota = answer.quota;
        if (answerQuota && sameGeneration) {
          // A `/quota` read still in flight — the re-read after a lost ask
          // (#1575) — started before this answer and would land after it,
          // overwriting the authoritative count with an older one. Cancel it
          // first and only then write: the cancel reverts asynchronously, so
          // without the await the revert would undo the write (Codex on
          // ready, #1635).
          await queryClient.cancelQueries({ queryKey: assistantQuotaQueryKey(orgId) });
          if (generationRef.current === generation) {
            // An answer's quota knows the ceiling, not the kill switch: keep
            // the stopped contours the last `/quota` read reported (#1076 review).
            queryClient.setQueryData<AssistantQuotaResponse>(
              assistantQuotaQueryKey(orgId),
              (prev) => ({ ...answerQuota, pausedScopes: prev?.pausedScopes })
            );
          } else {
            // The user moved during the cancel: the snapshot is no longer
            // this generation's to write — ask the server instead.
            void queryClient.invalidateQueries({
              queryKey: assistantQuotaQueryKey(orgId),
            });
          }
        } else {
          void queryClient.invalidateQueries({ queryKey: assistantQuotaQueryKey(orgId) });
        }

        // The key is spent by the answer whatever the generation: a retry
        // that succeeds after the user moved must not leave it to fetch this
        // turn again for a deliberate repeat (#937).
        spendLostAsk(idempotencyKey);
        if (!sameGeneration) return;

        setThreadId(answer.threadId);
        setFreshThread(false);
        setLocalTurns((prev) =>
          prev.map((turn) =>
            turn.turnId === placeholderId
              ? {
                  ...turn,
                  turnId: answer.turnId,
                  threadId: answer.threadId,
                  // As stored — after the PII pass — not as typed (#2449).
                  question: answer.question ?? turn.question,
                  status: answer.status,
                  answer: isAssistantTurnTerminal(answer.status) ? answer : null,
                }
              : turn
          )
        );
        if (isAssistantTurnTerminal(answer.status)) {
          // Settled by the reply itself: a history read that still lists the
          // turn in flight must not resume it (owner's review, 17.09).
          settledHereRef.current.add(answer.turnId);
          settle(answer, orgId);
        } else {
          setActiveTurn({
            turnId: answer.turnId,
            orgId,
            scope: subject.scope,
            startedAt,
          });
        }
      } catch (error) {
        // The key is settled BEFORE the generation guard: the user may have
        // walked to another record while the request was out, and the retry
        // back on this one must still ride the key — or must no longer, after
        // a verdict (#937). Only the panel's state belongs to the generation.
        if (!isAmbiguousFailure(error)) {
          // A 4xx is a verdict: the turn was rolled back or never created.
          spendLostAsk(idempotencyKey);
          if (generationRef.current !== generation) return;
        } else {
          // Still no verdict after the replay (or no time for one): the turn
          // may exist on the server. History is re-read BEFORE the composer
          // reopens. The turn created under this ask's key is this ask's —
          // the server's own word, never a guess from ids or clocks (Codex P2,
          // round 16): it is shown with its thread — also the new thread a
          // lost ask opened after `startNewThread`, which the "fresh" blank
          // would otherwise hide (Codex P2, round 14) — and polled if in
          // flight; there is no failure to report then.
          // Bounded: a read that stalls must not hold `asking` (Codex P2,
          // round 15) — past the bound, what history last said stands: it may
          // already list the turn, read while the ask was out. A read that
          // fails leaves `historyError` to keep the composer closed with a
          // retry.
          if (generationRef.current === generation) {
            const threads =
              (await withinMs(
                queryClient.fetchQuery(
                  assistantThreadsOptions(apiClient, orgId, subject)
                ),
                RECONCILE_READ_MS
              )) ??
              queryClient.getQueryData<AssistantThread[]>(
                assistantThreadsQueryKey(orgId, subject)
              );
            const landed = threads ? findLostTurnThread(threads, idempotencyKey) : null;
            if (landed && generationRef.current === generation) {
              recoverLostAsk(landed, orgId, idempotencyKey);
              setLocalTurns((prev) =>
                prev.filter((turn) => turn.turnId !== placeholderId)
              );
              setState('idle');
              return;
            }
          }
          // Not recognized: the key is kept for the same question asked again
          // (Codex P1, round 13). A history read that answers later, or one
          // that already listed the turn when an ask of an old generation
          // came back, is judged by the recovery effect above (Codex P2,
          // round 17).
          keepLostAsk(idempotencyKey, { contextKey, question, filters });
          // The server may have taken the turn and spent the last slot of the
          // day: re-read the ceiling now, not when the user walks back to this
          // record — on another one a fresh cache would still offer a question
          // the server then refuses (#1575). The re-read may also beat the
          // server's reservation and bring back the old number, which would
          // then count as fresh for the whole staleTime; so once it settles
          // the entry is marked invalid again without a fetch — the next
          // reader (the panel reopened, another record) asks the server again.
          const quotaKey = assistantQuotaQueryKey(orgId);
          void queryClient
            .invalidateQueries({ queryKey: quotaKey })
            .then(() =>
              queryClient.invalidateQueries({ queryKey: quotaKey, refetchType: 'none' })
            );
          if (generationRef.current !== generation) return;
        }
        setLocalTurns((prev) => prev.filter((turn) => turn.turnId !== placeholderId));
        setState('failed');
        const verdict = assistantRefusal(error);
        setRefusal(verdict);
        // The ceiling moved under the panel: re-read it, so a spent day closes
        // the composer instead of leaving yesterday's count on it.
        // Same for a contour stopped under it: the re-read closes the
        // composer instead of refusing every next question (#1076 review).
        if (verdict.code === 'quota_exceeded' || verdict.code === 'assistant_paused') {
          void queryClient.invalidateQueries({ queryKey: assistantQuotaQueryKey(orgId) });
        }
      }
    },
    [
      apiClient,
      currentThread,
      freshThread,
      queryClient,
      recoverLostAsk,
      selectedOrgId,
      settle,
      shownThreadId,
      subject,
      contextKey,
      keepLostAsk,
      spendLostAsk,
      threadId,
    ]
  );

  // Refused while an ask is in flight: dropping the active turn here would
  // not cancel the server's work, the generation guard would then discard
  // an answer that was paid for — and the composer would already accept a
  // second question (Codex P1, round 6).
  const startNewThread = useCallback(() => {
    if (state === 'asking' || activeTurn !== null) return;
    generationRef.current += 1;
    timedOutRef.current = null;
    // Lost asks are kept: their turns may land in the thread being left and
    // must still be recognized there (Codex P2, round 19).
    setFreshThread(true);
    setThreadId(null);
    setActiveTurn(null);
    setLocalTurns([]);
    setState('idle');
    setRefusal(null);
  }, [state, activeTurn]);

  const openThread = useCallback(
    (next: string) => {
      if (state === 'asking' || activeTurn !== null) return;
      generationRef.current += 1;
      timedOutRef.current = null;
      setFreshThread(false);
      setThreadId(next);
      setActiveTurn(null);
      setLocalTurns([]);
      setState('idle');
      setRefusal(null);
    },
    [state, activeTurn]
  );

  const blockedCode = useMemo(() => {
    const fromQuota = quotaQuery.error ? assistantErrorCode(quotaQuery.error) : null;
    if (fromQuota && BLOCKING_CODES.has(fromQuota)) return fromQuota;
    return errorCode && BLOCKING_CODES.has(errorCode) ? errorCode : null;
  }, [quotaQuery.error, errorCode]);

  // The kill switch may stop this contour alone (#1059): the panel says so
  // when it opens, not after a question was typed and refused.
  const paused = useMemo(
    // `failureReason`, not `error`: a 503 is retried, and the stop it names
    // must show during the retries, not after them.
    () =>
      quotaPausedScopes(quotaQuery.data, quotaQuery.failureReason).includes(
        subject.scope
      ),
    [quotaQuery.data, quotaQuery.failureReason, subject.scope]
  );

  // Only the shown thread's local copies — a verdict polled for a turn of
  // another thread must not surface in this conversation (Codex P2, round
  // 18). A copy with no thread yet is the ask that is opening one. A
  // placeholder whose key history already lists IS that turn: history's copy
  // shows (in flight, then answered), never a second bubble (owner's review,
  // 17.09).
  const shownLocalTurns = useMemo(() => {
    const shown = currentThread?.threadId ?? threadId;
    const landed = new Set(currentThread?.turns.map((turn) => turn.idempotencyKey) ?? []);
    return localTurns.filter(
      (turn) =>
        (turn.threadId === null || turn.threadId === shown) &&
        !(
          turn.turnId.startsWith(PLACEHOLDER_PREFIX) &&
          landed.has(turn.turnId.slice(PLACEHOLDER_PREFIX.length))
        )
    );
  }, [currentThread, localTurns, threadId]);

  const turns = useMemo(() => {
    const local = new Map(shownLocalTurns.map((turn) => [turn.turnId, turn]));
    // A verdict known here outranks a history copy that still says "in
    // progress"; once history has caught up it is the source of truth again.
    const history = (currentThread ? toView(currentThread) : []).map((turn) => {
      const mine = local.get(turn.turnId);
      return mine &&
        !isAssistantTurnTerminal(turn.status) &&
        isAssistantTurnTerminal(mine.status)
        ? mine
        : turn;
    });
    const known = new Set(history.map((turn) => turn.turnId));
    return [...history, ...shownLocalTurns.filter((turn) => !known.has(turn.turnId))];
  }, [currentThread, shownLocalTurns]);

  // Of the shown thread only: a question of the thread the user left is not
  // a repeat in this one (owner's review, 17.09).
  const askedHere = useMemo(
    () => shownLocalTurns.map((turn) => turn.typed ?? turn.question),
    [shownLocalTurns]
  );

  const isRepeat = useCallback(
    (question: string): boolean => {
      const asked = question.trim().toLowerCase();
      if (turns.some((turn) => turn.question.trim().toLowerCase() === asked)) return true;
      if (askedHere.some((own) => own.trim().toLowerCase() === asked)) return true;
      const shown = currentThread?.threadId ?? threadId;
      return (
        !!selectedOrgId && !!shown && wasQuestionAsked(selectedOrgId, shown, question)
      );
    },
    [turns, askedHere, currentThread, threadId, selectedOrgId]
  );

  // The client deadline passed here and the server has since settled the
  // turn (history is re-read at the server deadline): the verdict is in the
  // list, the timeout is over. A turn history no longer lists at all after a
  // fresh read (rolled back, gone) is dropped with the timeout — there is
  // nothing to wait for (owner's review, 17.09; was the settlement read).
  useEffect(() => {
    const stuck = timedOutRef.current;
    if (state !== 'timeout' || !stuck) return;
    if (
      turns.some(
        (turn) => turn.turnId === stuck.turnId && isAssistantTurnTerminal(turn.status)
      )
    ) {
      timedOutRef.current = null;
      setState('idle');
      return;
    }
    const readAfterTimeout = threadsQuery.dataUpdatedAt >= stuck.at;
    const listed = (threadsQuery.data ?? []).some((thread) =>
      thread.turns.some((turn) => turn.turnId === stuck.turnId)
    );
    if (readAfterTimeout && !listed) {
      timedOutRef.current = null;
      setLocalTurns((prev) => prev.filter((turn) => turn.turnId !== stuck.turnId));
      setState('idle');
    }
  }, [state, turns, threadsQuery.data, threadsQuery.dataUpdatedAt]);

  if (stale) return IDLE_RESULT;
  return {
    state,
    errorCode,
    refusal,
    blockedCode,
    paused,
    threadId: currentThread?.threadId ?? threadId,
    turns,
    threads: threadsQuery.data ?? [],
    isLoadingHistory: threadsQuery.isPending && threadsQuery.fetchStatus !== 'idle',
    historyError: threadsQuery.isError,
    reloadHistory: () => {
      void threadsQuery.refetch();
    },
    quota: quotaQuery.data ?? null,
    isRepeat,
    activeTurnId: activeTurn?.turnId ?? null,
    hasUnsettledWork: state === 'asking' || state === 'timeout' || activeTurn !== null,
    ask,
    startNewThread,
    openThread,
  };
}
